import React, { useState, useEffect, useRef } from 'react'
import {
  Shield,
  Search,
  Filter,
  MapPin,
  Calendar,
  ExternalLink,
  Download,
  Bell,
  CheckCircle,
  AlertTriangle,
  Building,
  Globe,
  FileText,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Table,
  Menu,
  X
} from 'lucide-react'
import { Subprocessor, SystemSettings } from '../lib/storage'
import { useTenant } from '../contexts/TenantContext'
import { tenantFetch } from '../lib/tenant-fetch'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import EmailCaptureGate from '../components/EmailCaptureGate'

const SubprocessorsPage = () => {
  const { currentTenant, loading: tenantLoading } = useTenant()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRegion, setFilterRegion] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [subprocessors, setSubprocessors] = useState<Subprocessor[]>([])
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [copySuccess, setCopySuccess] = useState<string | null>(null)
  const [showEmailGate, setShowEmailGate] = useState(false)
  const [emailSubmitted, setEmailSubmitted] = useState(false)
  const [pendingExportAction, setPendingExportAction] = useState<(() => void) | null>(null)

  // Navigation state and content for tabs
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [securityFeatures, setSecurityFeatures] = useState([])
  const [complianceBadges, setComplianceBadges] = useState([])
  const [knowledgeBase, setKnowledgeBase] = useState<any>(null)
  const [documents, setDocuments] = useState([])

  // Store timeout ID for copy success message
  const copySuccessTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copySuccessTimeoutRef.current) {
        clearTimeout(copySuccessTimeoutRef.current)
      }
    }
  }, [])

  // Load data on component mount
  useEffect(() => {
    // Don't fetch until tenant is loaded
    if (tenantLoading || !currentTenant) {
      return
    }

    const loadData = async () => {
      try {
        setLoading(true)

        // Fetch all content in parallel with tenant context
        const [badgesRes, featuresRes, kbRes, documentsRes, subprocessorsRes, settingsRes] = await Promise.allSettled([
          tenantFetch('/api/badges', { tenantId: currentTenant.id }),
          tenantFetch('/api/security-features', { tenantId: currentTenant.id }),
          tenantFetch(`/api/get-kb?tenantId=${currentTenant.id}`),
          tenantFetch('/api/documents', { tenantId: currentTenant.id }),
          tenantFetch('/api/subprocessors', { tenantId: currentTenant.id }),
          tenantFetch('/api/settings-db', { tenantId: currentTenant.id })
        ])

        // Handle compliance badges
        if (badgesRes.status === 'fulfilled' && badgesRes.value.ok) {
          const badgesData = await badgesRes.value.json()
          const visibleBadges = (badgesData || []).filter((badge: any) =>
            !badge.hidden && !badge.archived && !badge.deleted
          )
          setComplianceBadges(visibleBadges)
        }

        // Handle security features
        if (featuresRes.status === 'fulfilled' && featuresRes.value.ok) {
          const featuresData = await featuresRes.value.json()
          const features = featuresData.success ? featuresData.result.features : featuresData
          const visibleFeatures = (features || []).filter((f: any) =>
            !f.archived && !f.deleted
          )
          setSecurityFeatures(visibleFeatures)
        }

        // Handle knowledge base
        if (kbRes.status === 'fulfilled' && kbRes.value.ok) {
          const kbData = await kbRes.value.json()
          setKnowledgeBase(kbData)
        }

        // Handle documents
        if (documentsRes.status === 'fulfilled' && documentsRes.value.ok) {
          const documentsData = await documentsRes.value.json()
          const docs = documentsData.success ? documentsData.result.documents : documentsData
          const visibleDocs = (docs || []).filter((doc: any) =>
            !doc.archived && !doc.deleted
          )
          setDocuments(visibleDocs)
        }

        // Handle subprocessors
        if (subprocessorsRes.status === 'fulfilled' && subprocessorsRes.value.ok) {
          try {
            const subprocessorsData = await subprocessorsRes.value.json()
            // Handle multiple possible response formats
            let vendors = []
            if (Array.isArray(subprocessorsData)) {
              vendors = subprocessorsData
            } else if (subprocessorsData.subprocessors && Array.isArray(subprocessorsData.subprocessors)) {
              vendors = subprocessorsData.subprocessors
            } else if (subprocessorsData.result?.subprocessors && Array.isArray(subprocessorsData.result.subprocessors)) {
              // API returns { success: true, result: { subprocessors: [...] } }
              vendors = subprocessorsData.result.subprocessors
            } else if (subprocessorsData.result?.vendors && Array.isArray(subprocessorsData.result.vendors)) {
              vendors = subprocessorsData.result.vendors
            }
            // Filter to only show active subprocessors (not archived or deleted) in public view
            const visibleSubprocessors = vendors.filter((s: Subprocessor) =>
              s && s.status === 'active' && !s.archived && !s.deleted
            )
            setSubprocessors(visibleSubprocessors)
          } catch (err) {
            console.error('Failed to parse subprocessors:', err)
            setSubprocessors([])
          }
        }

        // Handle system settings
        if (settingsRes.status === 'fulfilled' && settingsRes.value.ok) {
          const settingsData = await settingsRes.value.json()
          const settings = settingsData.success ? settingsData.result : settingsData
          // Settings API returns a single object, not an array
          if (settings && settings.id) {
            setSystemSettings(settings)
          }
        }
      } catch (err) {
        console.error('Error loading data:', err)
        setError('Failed to load page data')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [currentTenant, tenantLoading])

  const filteredSubprocessors = subprocessors.filter(processor => {
    const matchesSearch = searchTerm === '' ||
      processor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      processor.purpose.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesRegion = filterRegion === 'all' ||
      processor.region.toLowerCase().includes(filterRegion.toLowerCase())

    const matchesType = filterType === 'all' || processor.type === filterType

    return matchesSearch && matchesRegion && matchesType
  })

  // Get all unique attestations across all subprocessors
  const allAttestations = Array.from(
    new Set(
      subprocessors.flatMap(p => p.attestations?.map(a => a.name) || [])
    )
  ).sort()

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const hasAttestation = (processor: Subprocessor, attestationName: string) => {
    return processor.attestations?.some(a => a.name === attestationName)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'inactive':
        return <AlertTriangle className="h-4 w-4 text-red-600" />
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'inactive':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleEmailSubmit = (email: string, company: string) => {
    setEmailSubmitted(true)
    setShowEmailGate(false)
    // Execute the pending export action
    if (pendingExportAction) {
      pendingExportAction()
      setPendingExportAction(null)
    }
  }

  const requireEmailForExport = (exportAction: () => void) => {
    if (emailSubmitted) {
      exportAction()
    } else {
      setPendingExportAction(() => exportAction)
      setShowEmailGate(true)
    }
  }

  // Export functions
  const generateCSV = (processors: Subprocessor[]) => {
    const headers = ['Name', 'Type', 'Purpose', 'Region', 'Status', 'Data Types', 'Attestations', 'Last Reviewed', 'Next Review', 'DPA URL', 'Privacy URL']
    const rows = processors.map(p => [
      p.name,
      p.type,
      p.purpose,
      p.region,
      p.status,
      p.dataTypes.join('; '),
      p.attestations?.map(a => a.name).join('; ') || '',
      new Date(p.lastReviewed).toLocaleDateString(),
      new Date(p.nextReview).toLocaleDateString(),
      p.dpaUrl || '',
      p.privacyUrl || ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    return csvContent
  }

  const generateTable = (processors: Subprocessor[]) => {
    const headers = ['Name', 'Type', 'Purpose', 'Region', 'Status', 'Data Types', 'Attestations', 'Last Reviewed', 'Next Review']
    const rows = processors.map(p => [
      p.name,
      p.type,
      p.purpose,
      p.region,
      p.status,
      p.dataTypes.join(', '),
      p.attestations?.map(a => a.name).join(', ') || '',
      new Date(p.lastReviewed).toLocaleDateString(),
      new Date(p.nextReview).toLocaleDateString()
    ])

    // Tab-separated format for pasting into Word/Docs
    return [
      headers.join('\t'),
      ...rows.map(row => row.join('\t'))
    ].join('\n')
  }

  const downloadCSV = () => {
    requireEmailForExport(() => {
      const csv = generateCSV(filteredSubprocessors)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `subprocessors-${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      setShowExportMenu(false)
      showCopySuccess('CSV downloaded')
    })
  }

  const copyAsCSV = async () => {
    requireEmailForExport(async () => {
      const csv = generateCSV(filteredSubprocessors)
      try {
        await navigator.clipboard.writeText(csv)
        showCopySuccess('CSV copied to clipboard')
      } catch (err) {
        console.error('Failed to copy:', err)
        alert('Failed to copy to clipboard')
      }
      setShowExportMenu(false)
    })
  }

  const copyAsTable = async () => {
    requireEmailForExport(async () => {
      const table = generateTable(filteredSubprocessors)
      try {
        await navigator.clipboard.writeText(table)
        showCopySuccess('Table copied to clipboard')
      } catch (err) {
        console.error('Failed to copy:', err)
        alert('Failed to copy to clipboard')
      }
      setShowExportMenu(false)
    })
  }

  const downloadPDF = () => {
    requireEmailForExport(() => {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      })

      // Add header
      const companyName = currentTenant?.name || systemSettings?.companyName || 'Company'
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.text('Subprocessor List', 14, 15)

      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.text(companyName, 14, 22)
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28)

      // Prepare table data
      const tableHeaders = [['Name', 'Type', 'Purpose', 'Region', 'Status', 'Data Types', 'Last Reviewed']]
      const tableData = filteredSubprocessors.map(p => [
        p.name,
        p.type,
        p.purpose.length > 60 ? p.purpose.substring(0, 60) + '...' : p.purpose,
        p.region,
        p.status,
        p.dataTypes.join(', '),
        new Date(p.lastReviewed).toLocaleDateString()
      ])

      // Add table
      autoTable(doc, {
        startY: 35,
        head: tableHeaders,
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'left'
        },
        columnStyles: {
          0: { cellWidth: 40 }, // Name
          1: { cellWidth: 25 }, // Type
          2: { cellWidth: 60 }, // Purpose
          3: { cellWidth: 30 }, // Region
          4: { cellWidth: 25 }, // Status
          5: { cellWidth: 50 }, // Data Types
          6: { cellWidth: 30 }  // Last Reviewed
        },
        margin: { left: 14, right: 14 }
      })

      // Add footer with page numbers
      const pageCount = (doc as any).internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        )
      }

      // Save PDF
      doc.save(`subprocessors-${new Date().toISOString().split('T')[0]}.pdf`)
      setShowExportMenu(false)
      showCopySuccess('PDF downloaded')
    })
  }

  const showCopySuccess = (message: string) => {
    setCopySuccess(message)
    if (copySuccessTimeoutRef.current) clearTimeout(copySuccessTimeoutRef.current)
    copySuccessTimeoutRef.current = setTimeout(() => {
      setCopySuccess(null)
      copySuccessTimeoutRef.current = null
    }, 3000)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading subprocessors...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dynamic Theme Styles */}
      {systemSettings && (
        <style>
          {`
            :root {
              --primary-500: ${systemSettings.primaryColor || '#2E51ED'};
              --netlify-500: ${systemSettings.primaryColor || '#2E51ED'};
              --secondary-color: ${systemSettings.secondaryColor || '#05BDBA'};
            }
          `}
        </style>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                {systemSettings?.logoUrl ? (
                  <img src={systemSettings.logoUrl} alt="Logo" className="w-8 h-8 rounded" />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-800 rounded flex items-center justify-center">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {systemSettings?.brandName || systemSettings?.companyName || currentTenant?.name || 'Trust Center'}
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 -mt-1">Trust Center</p>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <a
                href="/"
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Overview
              </a>
              {securityFeatures.length > 0 && (
                <a
                  href="/"
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Security
                </a>
              )}
              {complianceBadges.length > 0 && (
                <a
                  href="/"
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Compliance
                </a>
              )}
              {systemSettings?.privacyPolicyUrl && (
                <a
                  href="/"
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Privacy
                </a>
              )}
              {subprocessors.length > 0 && (
                <a
                  href={systemSettings?.subprocessorsPageUrl || '/subprocessors'}
                  className="text-sm font-medium text-primary-600"
                >
                  Subprocessors
                </a>
              )}
              {knowledgeBase && Object.keys(knowledgeBase.answers || {}).length > 0 && (
                <a
                  href="/"
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Knowledge Base
                </a>
              )}
              {documents.length > 0 && (
                <a
                  href="/"
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Library
                </a>
              )}
            </nav>

            {/* Desktop CTA Button */}
            <div className="hidden md:block">
              {localStorage.getItem('auth-token') ? (
                <button
                  onClick={() => window.location.href = '/profile'}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  My Profile
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={() => window.location.href = '/login'}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  Sign In
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 pt-2 border-t border-gray-200">
              <nav className="flex flex-col space-y-3">
                <a
                  href="/"
                  className="text-left px-4 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100"
                >
                  Overview
                </a>
                {securityFeatures.length > 0 && (
                  <a
                    href="/"
                    className="text-left px-4 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Security
                  </a>
                )}
                {complianceBadges.length > 0 && (
                  <a
                    href="/"
                    className="text-left px-4 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Compliance
                  </a>
                )}
                {systemSettings?.privacyPolicyUrl && (
                  <a
                    href="/"
                    className="text-left px-4 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Privacy
                  </a>
                )}
                {subprocessors.length > 0 && (
                  <a
                    href={systemSettings?.subprocessorsPageUrl || '/subprocessors'}
                    className="text-left px-4 py-2 rounded-md text-base font-medium bg-primary-50 text-primary-600"
                  >
                    Subprocessors
                  </a>
                )}
                {knowledgeBase && Object.keys(knowledgeBase.answers || {}).length > 0 && (
                  <a
                    href="/"
                    className="text-left px-4 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Knowledge Base
                  </a>
                )}
                {documents.length > 0 && (
                  <a
                    href="/"
                    className="text-left px-4 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Library
                  </a>
                )}
                <div className="pt-2 border-t border-gray-200">
                  {localStorage.getItem('auth-token') ? (
                    <button
                      onClick={() => window.location.href = '/profile'}
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                    >
                      My Profile
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => window.location.href = '/login'}
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                    >
                      Sign In
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </button>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">
              {systemSettings?.subprocessorsHeroTitle || `${systemSettings?.companyName || 'Company'} Subprocessors`}
            </h1>
            {systemSettings?.subprocessorsHeroSubtitle && (
              <h2 className="text-2xl text-primary-200 mb-4">
                {systemSettings.subprocessorsHeroSubtitle}
              </h2>
            )}
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              {systemSettings?.subprocessorsHeroDescription ||
               'Transparency in our partnerships. We work with carefully selected third-party vendors to provide our services while maintaining the highest standards of security and compliance.'}
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 text-sm">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-300 mr-2" />
                <span>{subprocessors.filter(p => p.isActive && p.status === 'active').length} Active Partners</span>
              </div>
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-primary-300 mr-2" />
                <span>100% DPA Coverage</span>
              </div>
              <div className="flex items-center">
                <Globe className="h-5 w-5 text-primary-300 mr-2" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Our Data Processing Partners</h2>
          <p className="text-gray-600 mb-4">
            This page provides a comprehensive list of all third-party subprocessors that {systemSettings?.companyName || 'we'} work with
            to deliver our services. Each partner has been carefully vetted and is bound by strict data processing agreements
            to ensure your data remains secure and compliant with global privacy regulations.
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <span>All subprocessors under DPA</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <span>Regular security reviews</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <span>GDPR compliant processing</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <span>30-day change notifications</span>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search subprocessors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <select
                value={filterRegion}
                onChange={(e) => setFilterRegion(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Regions</option>
                <option value="us">US</option>
                <option value="eu">EU</option>
                <option value="global">Global</option>
              </select>

              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Types</option>
                <option value="Infrastructure">Infrastructure</option>
                <option value="Security">Security</option>
                <option value="Monitoring">Monitoring</option>
                <option value="Communication">Communication</option>
                <option value="Payment">Payment</option>
                <option value="Support">Support</option>
                <option value="Analytics">Analytics</option>
                <option value="Development">Development</option>
              </select>
            </div>

            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>Showing {filteredSubprocessors.length} of {subprocessors.length}</span>
              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export
                  <ChevronDown className="h-4 w-4 ml-1" />
                </button>

                {showExportMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowExportMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                      <div className="py-1">
                        <button
                          onClick={downloadCSV}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <Download className="h-4 w-4 mr-3 text-gray-400" />
                          <div>
                            <div className="font-medium">Download CSV</div>
                            <div className="text-xs text-gray-500">Open in Excel or Sheets</div>
                          </div>
                        </button>
                        <button
                          onClick={copyAsCSV}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <Copy className="h-4 w-4 mr-3 text-gray-400" />
                          <div>
                            <div className="font-medium">Copy as CSV</div>
                            <div className="text-xs text-gray-500">Paste into spreadsheet</div>
                          </div>
                        </button>
                        <button
                          onClick={copyAsTable}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <Table className="h-4 w-4 mr-3 text-gray-400" />
                          <div>
                            <div className="font-medium">Copy as Table</div>
                            <div className="text-xs text-gray-500">Paste into Word or Docs</div>
                          </div>
                        </button>
                        <button
                          onClick={downloadPDF}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <FileText className="h-4 w-4 mr-3 text-gray-400" />
                          <div>
                            <div className="font-medium">Download PDF</div>
                            <div className="text-xs text-gray-500">Professional document</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {copySuccess && (
                <div className="flex items-center text-green-600 animate-fade-in">
                  <Check className="h-4 w-4 mr-1" />
                  {copySuccess}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subprocessors Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                    Subprocessor
                  </th>
                  {allAttestations.map((attestation) => (
                    <th
                      key={attestation}
                      className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                      title={attestation}
                    >
                      <div className="flex flex-col items-center">
                        <span className="whitespace-nowrap">{attestation}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubprocessors.map((processor) => (
                  <React.Fragment key={processor.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 sticky left-0 bg-white z-10">
                        <button
                          onClick={() => toggleRow(processor.id)}
                          className="flex items-center space-x-2 text-left w-full group"
                        >
                          {expandedRows.has(processor.id) ? (
                            <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                          <div>
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-900 group-hover:text-primary-600">
                                {processor.name}
                              </span>
                              <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(processor.status)}`}>
                                {processor.status}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">{processor.type}</div>
                          </div>
                        </button>
                      </td>
                      {allAttestations.map((attestation) => (
                        <td key={attestation} className="px-3 py-4 text-center">
                          {hasAttestation(processor, attestation) && (
                            <CheckCircle className="h-5 w-5 text-green-600 mx-auto" title={`${processor.name} has ${attestation}`} />
                          )}
                        </td>
                      ))}
                    </tr>
                    {expandedRows.has(processor.id) && (
                      <tr>
                        <td colSpan={allAttestations.length + 1} className="px-6 py-4 bg-gray-50">
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">Purpose</h4>
                              <p className="text-sm text-gray-600">{processor.purpose}</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Data Types</h4>
                                <div className="flex flex-wrap gap-1">
                                  {processor.dataTypes.map((type, index) => (
                                    <span key={index} className="inline-block bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded">
                                      {type}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Regional Processing</h4>
                                <div className="flex items-center text-sm text-gray-600">
                                  <MapPin className="h-4 w-4 mr-1" />
                                  {processor.region}
                                </div>
                              </div>

                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Review Dates</h4>
                                <div className="space-y-1 text-sm text-gray-600">
                                  <div className="flex items-center">
                                    <Calendar className="h-4 w-4 mr-1" />
                                    Last: {new Date(processor.lastReviewed).toLocaleDateString()}
                                  </div>
                                  <div className="flex items-center">
                                    <Calendar className="h-4 w-4 mr-1" />
                                    Next: {new Date(processor.nextReview).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center space-x-6">
                              {processor.dpaUrl && (
                                <a
                                  href={processor.dpaUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                                >
                                  <FileText className="h-4 w-4 mr-1" />
                                  Data Processing Agreement
                                  <ExternalLink className="h-3 w-3 ml-1" />
                                </a>
                              )}
                              {processor.privacyUrl && (
                                <a
                                  href={processor.privacyUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                                >
                                  <Shield className="h-4 w-4 mr-1" />
                                  Privacy Policy
                                  <ExternalLink className="h-3 w-3 ml-1" />
                                </a>
                              )}
                            </div>

                            {processor.changeHistory && processor.changeHistory.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                  Recent Changes ({processor.changeHistory.length})
                                </h4>
                                <div className="space-y-2">
                                  {processor.changeHistory.slice(0, 3).map((change, index) => (
                                    <div key={index} className="flex items-center text-sm">
                                      <div className="w-2 h-2 bg-primary-600 rounded-full mr-3"></div>
                                      <span className="text-gray-600">{new Date(change.date).toLocaleDateString()}</span>
                                      <span className="mx-2">—</span>
                                      <span className="text-gray-900">{change.change}</span>
                                      <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                        change.type === 'review' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                      }`}>
                                        {change.type}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Information */}
        <div className="mt-12 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Stay Updated on Changes</h3>
          <p className="text-gray-600 mb-4">
            {systemSettings?.companyName || 'We'} notify customers of any changes to our subprocessor list at least 30 days in advance.
            Subscribe to updates to receive notifications about subprocessor changes and ensure you're always informed
            about how your data is being processed.
          </p>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
              <Bell className="h-4 w-4 mr-2" />
              Email Notifications
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              <Globe className="h-4 w-4 mr-2" />
              RSS Feed
            </button>
            {systemSettings?.supportEmail && (
              <a
                href={`mailto:${systemSettings.supportEmail}?subject=Subprocessor Inquiry`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FileText className="h-4 w-4 mr-2" />
                Contact Support
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Email Capture Gate */}
      {showEmailGate && (
        <EmailCaptureGate
          documentName="Subprocessor List"
          onSubmit={handleEmailSubmit}
          onClose={() => setShowEmailGate(false)}
        />
      )}
    </div>
  )
}

export default SubprocessorsPage